// ============================================================
// API Types — derived from backend Pydantic schemas
// ============================================================

export interface PaginatedResponse<T> {
  items: T[];
  count: number;
}

// --- ERROR ---

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

// --- COMMON CLINICAL TYPES ---

export interface MetadataAnamnesis {
  alergias: Record<string, boolean>;
  alergias_otras: string;
  enfermedades: Record<string, boolean>;
  enfermedades_otras: string;
  antecedentes_odonto: Record<string, boolean>;
  habitos: Record<string, boolean>;
  higiene: string;
  medicacion: string;
  cirugias_previas: string;
  motivo_consulta: string;
}

export interface FirmaCoord {
  trace: { x: number; y: number; time?: number }[][];
  image_data_url: string;
  timestamp: string;
  paciente_dni: string;
}

export interface FirmaCoord {
  trace: { x: number; y: number; time?: number }[][];
  image_data_url: string;
  timestamp: string;
  paciente_dni: string;
}

// --- AUTH ---

export interface LoginIn {
  username: string;
  password: string;
}

export interface LoginOut {
  token: string;
  rol: string;
  empresa_id: string;
  nombre: string;
}

// --- PACIENTE ---

export interface AlertaMedicaOut {
  id: string;
  categoria: string;
  severidad: number;
  activa: boolean;
  descripcion: string | null;
  created_at: string;
}

export interface AlertaMedicaIn {
  categoria: string;
  descripcion: string;
  severidad: number;
  activa?: boolean;
}

export interface AlertaMedicaPatchIn {
  categoria?: string;
  descripcion?: string;
  severidad?: number;
  activa?: boolean;
}

export interface TurnoResumenBrief {
  fecha_hora: string;
  procedimiento_nombre: string;
}

export interface PacienteIn {
  dni: string;
  nombre: string;
  apellido: string;
  fecha_nacimiento?: string | null;
  genero?: string;
  email?: string | null;
  telefono?: string;
  direccion?: string;
  pipeline?: string;
  sede_id?: string | null;
  contacto_emergencia_nombre?: string;
  contacto_emergencia_tel?: string;
  // Legacy — ignorados por el backend, mantenidos para compatibilidad
  tiene_alerta?: boolean;
  alerta_nivel?: number;
  alerta_nota?: string;
}

export interface PacienteOut {
  dni: string;
  nombre: string;
  apellido: string;
  fecha_nacimiento: string | null;
  genero: string;
  email: string | null;
  telefono: string;
  direccion: string;
  pipeline: string;
  sede_id: string;
  contacto_emergencia_nombre: string;
  contacto_emergencia_tel: string;
  created_at: string;
  updated_at: string;
  // Alertas médicas estructuradas
  alertas_activas: AlertaMedicaOut[];
  tiene_alerta_critica: boolean;
  tiene_alerta: boolean;
  alerta_critica_categorias: string[];
  // Contacto rápido
  whatsapp_url: string | null;
  // Financiero
  saldo_pendiente: string;
  // Turnos resumen
  ultimo_turno: TurnoResumenBrief | null;
  proximo_turno: TurnoResumenBrief | null;
  // 360
  ordenes_laboratorio: OrdenLaboratorioOut[];
}

// --- TURNO ---

export interface TurnoIn {
  paciente_dni: string;
  sede_id: string;
  procedimiento_id: string;
  profesional_id: number;
  fecha_hora: string;
  duracion_min: number;
  box: number;
  estado?: string;
  force?: boolean;
}

export interface TurnoOut {
  id: string;
  paciente_dni: string;
  paciente_nombre: string;
  paciente_telefono: string;
  paciente_alerta: number;
  sede_id: string;
  sede_nombre: string;
  procedimiento_id: string;
  procedimiento_nombre: string;
  profesional_id: number;
  profesional_nombre: string;
  fecha_hora: string;
  duracion_min: number;
  box: number;
  estado: string;
  notas: string | null;
  monto_reserva: string;
  /** ISO datetime — solo presente cuando estado === 'reservado_temporal' */
  reserva_expira_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PagoReservaIn {
  monto: string | number;
  metodo_pago: string;
}

// --- STOCK ---

export interface StockWarning {
  insumo_id: string;
  nombre: string;
  stock_actual: string;
  stock_minimo: string;
}

export interface CompletarTurnoResponse {
  success: boolean;
  message: string;
  warnings: StockWarning[];
  requiere_informe?: boolean;
}

// --- HISTORIA CLINICA ---

export interface TurnoResumenOut {
  id: string;
  fecha_hora: string;
  procedimiento_nombre: string;
}

export interface HistoriaClinicaOut {
  id: string;
  paciente_dni: string;
  profesional_nombre: string;
  tipo: string;
  titulo: string;
  contenido: string;
  metadata: MetadataAnamnesis | Record<string, unknown>;
  turno_id: string | null;
  turno: TurnoResumenOut | null;
  pendiente_completar: boolean;
  fecha: string;
}

export interface InformeQuirurgicoIn {
  turno_id: string;
  piezas_intervenidas: string[];
  tecnica_quirurgica: string;
  hallazgos: string;
  medicamentos_insumos: string;
  indicaciones_postoperatorias: string;
  sugerir_implante?: boolean;
}

// --- ODONTOGRAMA ---

export interface DienteOut {
  fdi_number: number;
  estado: string;
  estado_json: any;
  caras: Record<string, unknown>;
  updated_at: string;
}

export interface DienteBulkIn {
  fdi: number;
  estado?: string;
  caras?: Record<string, unknown>;
  estado_json?: Record<string, unknown>;
}

// --- LABORATORIO ---

export interface ProveedorLaboratorioOut {
  id: string;
  nombre: string;
  especialidad: string;
  whatsapp: string;
  activo: boolean;
}

export interface OrdenLaboratorioOut {
  id: string;
  presupuesto_id: string;
  diente_fdi: number | null;
  proveedor_nombre: string;
  tipo_trabajo: string;
  costo_laboratorio: string;
  estado: string; // Renamed from estado_envio
  fecha_envio: string | null;
  fecha_prometida: string | null;
  paciente_dni: string;
  paciente_nombre: string;
}

export interface OrdenLaboratorioIn {
  presupuesto_id: string;
  diente_fdi?: number | null;
  proveedor_id: string;
  tipo_trabajo: string;
  costo_laboratorio: string | number;
  fecha_envio?: string | null;
  fecha_prometida?: string | null;
}

// --- PIPELINE ---

export type PipelineEstado =
  | 'en_diagnostico'
  | 'en_tratamiento'
  | 'post_op'
  | 'alta_medica'
  | 'inactivo'

// --- MARKETING ---

export interface EventoFuturoIn {
  paciente_dni: string
  fecha_ejecucion: string  // ISO date YYYY-MM-DD
  motivo: string
  mensaje_sugerido?: string
  turno_id?: string | null
  notas?: string
}

export interface EventoFuturoOut {
  id: number;
  paciente_dni: string;
  paciente_nombre: string;
  fecha_ejecucion: string;
  motivo: string;
  motivo_display: string;
  mensaje_sugerido: string;
  estado: string;
  notas: string;
  turno_id: string | null;
  created_at: string;
}

export interface PostTurnoModalPayload {
  opcion: 'A' | 'B' | 'C' | 'omitir'
  turno_id: string
  evento_futuro_data?: EventoFuturoIn
  nuevo_turno_data?: TurnoIn
}

export interface MensajeMarketingOut {
  id: string;
  paciente_dni: string;
  tipo_evento: string;
  contenido_mensaje: string;
  estado_envio: string;
  usuario_nombre: string | null;
  fecha_envio: string;
}

export interface MensajeManualIn {
  paciente_dni: string;
  canal: 'whatsapp' | 'email' | 'sms';
  tipo: string;
  mensaje: string;
  programado_para: string;
  requiere_autorizacion: boolean;
  notas?: string;
}

export interface MensajeManualOut {
  id: string;
  paciente_dni: string;
  paciente_nombre: string;
  canal: string;
  tipo: string;
  mensaje: string;
  programado_para: string;
  requiere_autorizacion: boolean;
  estado: string;
  notas: string;
  autorizado_por_nombre: string | null;
  autorizado_at: string | null;
  enviado_at: string | null;
  creado_por_nombre: string | null;
  created_at: string;
}

export interface MarketingKPIOut {
  total: number;
  enviados: number;
  programados: number;
  pendientes: number;
  por_canal_whatsapp: number;
  por_canal_email: number;
  por_canal_sms: number;
}

// --- INSUMOS Y ESCANDALLO ---

export interface InsumoIn {
  nombre: string;
  unidad_medida: string;
  costo_unitario: string | number;
  stock_actual?: string | number;
  stock_minimo?: string | number;
  marca?: string;
  lote_actual?: string;
  es_trazable?: boolean;
  fecha_vencimiento?: string | null;
  categoria?: string;
}

export interface InsumoOut {
  id: string;
  nombre: string;
  unidad_medida: string;
  costo_unitario: string;
  stock_actual: string;
  stock_minimo: string;
  marca: string;
  lote_actual: string;
  es_trazable: boolean;
  fecha_vencimiento: string | null;
  categoria: string;
  nivel_alerta: "ok" | "bajo" | "agotado";
  created_at: string;
  updated_at: string;
}

export interface MovimientoStockIn {
  tipo: "entrada" | "salida" | "ajuste";
  cantidad: string | number;
  motivo?: string;
  lote?: string;
}

export interface MovimientoStockOut {
  id: string;
  tipo: string;
  cantidad: string;
  motivo: string;
  lote: string | null;
  fecha: string;
}

export interface InsumosKPIOut {
  total_items: number;
  agotados: number;
  bajos: number;
  ok: number;
  valor_inventario: string;
}

export interface EscandalloIn {
  insumo_id: string;
  cantidad_requerida: string | number;
}

export interface EscandalloOut {
  id: string;
  insumo_nombre: string;
  cantidad_requerida: string;
  costo_estimado: string;
}

// --- GASTOS FIJOS ---

export interface GastoFijoIn {
  nombre: string;
  categoria: string;
  monto: string | number;
  fecha_pago?: string;
  notas?: string;
}

export interface GastoFijoOut {
  id: string;
  nombre: string;
  categoria: string;
  categoria_display: string;
  monto: string;
  fecha_pago: string;
  notas: string;
  created_at: string;
}

export interface ResumenFinancieroOut {
  mes: number
  anio: number
  ingresos: string
  egresos: string
  resultado: string
  ingresos_mes_anterior: string
  egresos_mes_anterior: string
  resultado_mes_anterior: string
}

// --- PROCEDIMIENTOS ---

export interface ProcedimientoIn {
  nombre: string;
  categoria?: string;
  precio_base: string | number;
  precio_hasta?: string | number | null;
  duracion_min?: number;
  color?: string;
  precio_fijo?: boolean;
  requiere_poscx?: boolean;
  costo_laboratorio_estimado?: string | number;
  descripcion_ia?: string;
  activo?: boolean;
}

export interface ProcedimientoOut {
  id: string;
  nombre: string;
  categoria: string;
  precio_base: string;
  precio_hasta: string | null;
  duracion_min: number;
  color: string;
  precio_fijo: boolean;
  requiere_poscx: boolean;
  costo_laboratorio_estimado: string;
  descripcion_ia: string;
  activo: boolean;
  profesionales: ProfesionalBriefOut[];
  created_at: string;
  updated_at: string;
}

// --- USUARIOS / PROFESIONALES ---

export interface ProfesionalBriefOut {
  id: number;
  nombre: string;
  color_agenda: string;
  rol: string;
}

export interface UsuarioOut {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  rol: string;
  color_agenda: string;
  activo: boolean;
  foto_url: string;
  modulos_habilitados: string[] | null;
}

export interface UsuarioIn {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  rol: string;
  color_agenda?: string;
  activo?: boolean;
  password?: string;
  modulos_habilitados?: string[] | null;
}

// --- SEDES ---

export interface SedeOut {
  id: string;
  nombre: string;
  direccion: string;
  telefono: string;
  email: string;
  es_principal: boolean;
  activa: boolean;
  capacidad_maxima: number;
  horarios_atencion: Record<string, { activo: boolean; hora_inicio: string; hora_fin: string }>;
  dias_bloqueados: Array<{ fecha: string; motivo: string }>;
  profesionales_ids: number[];
}

// --- CATÁLOGO (Admin) ---

export interface EscandalloItemOut {
  insumo_id: string;
  insumo_nombre: string;
  cantidad_requerida: string;
  costo_estimado: string;
}

export interface EscandalloItemIn {
  insumo_id: string;
  cantidad_requerida: string | number;
}

export interface EscandalloReplaceIn {
  items: EscandalloItemIn[];
}

export interface ConfiguracionCuotasOut {
  cuotas_enabled: boolean;
  cuotas_cantidad: number;
}

export interface ConfiguracionCuotasIn {
  cuotas_enabled: boolean;
  cuotas_cantidad: number;
}

export interface SignatureIn {
  tipo: string;
  contenido: string;
  firma_coords: FirmaCoord | Record<string, unknown>;
}

export interface DocumentoFirmadoOut {
  id: string;
  paciente_dni: string;
  tipo: string;
  archivo_pdf_url: string;
  hash_integridad: string;
  created_at: string;
}

export interface DocumentoFirmadoListOut {
  id: string;
  paciente_dni: string;
  tipo: string;
  hash_integridad: string;
  created_at: string;
}

export interface DocumentoGlobalOut {
  id: string;
  tipo_fuente: 'firma' | 'adjunto_historia';
  tipo_doc: string;
  nombre: string;
  url: string;
  fecha: string;
  metadata: Record<string, unknown>;
}

// --- DICTADO IA (STT local + LLM) ---
export interface DictadoPieza {
  fdi: number;
  estado: string;
  caras?: Record<string, boolean>;
}

/** Campo reportado en el preview v2 (faltante o incierto) — espejo DictadoCampoFuera. */
export interface DictadoCampoFuera {
  key: string;
  label: string;
  requerido?: boolean;
}

/**
 * Preview estructurado dictado v2 (backend t_6f227c56): `campos` trae SOLO los
 * campos reales del formulario del tipo seleccionado. `nota` queda para compat
 * v1 (== campos.hallazgos en evolución).
 */
export interface DictadoPreviewOut {
  texto_transcripto: string;
  duracion_audio_seg: number;
  tipo: string;
  tipo_nombre: string;
  titulo: string;
  campos: Record<string, unknown>;
  piezas: DictadoPieza[];
  alertas: string[];
  campos_faltantes: DictadoCampoFuera[];
  campos_inciertos: DictadoCampoFuera[];
  errores_validacion: string[];
  nota: string;
}

export interface DictadoApplyIn {
  /** OBLIGATORIO en v2: sin tipo no se persiste nada (feedback Julio 10/10). */
  tipo: string;
  titulo: string;
  campos: Record<string, unknown>;
  /** compat v1 — el v2 manda `campos`; se conserva para no romper llamadores viejos */
  nota?: string;
  piezas: DictadoPieza[];
  alertas: string[];
  texto_transcripto: string;
  turno_id?: string | null;
  /** prescripcion: trazos biométricos {'trace': [[{x,y}]], image_data_url, timestamp, paciente_dni} */
  firma_coords?: Record<string, unknown> | null;
}

export interface DictadoApplyOut {
  /** presente en evolucion/anamnesis/evaluacion/acto_quirurgico (HistoriaClinicaEntry) */
  historia_id?: string | null;
  /** presente en prescripcion (DocumentoFirmado tipo receta) */
  documento_firmado_id?: string | null;
  tipo: string;
  odontograma_updates: number;
  piezas_descartadas: number[];
}

// --- ODONTOGRAMA PATCH ---
export interface DientePatchIn {
  /** Omitido = derivar del estado_json (backend fix 10/10: ya no pisa V1 con 'sano' silencioso) */
  estado?: string;
  caras?: Record<string, boolean>;
  estado_json?: Record<string, unknown>;
}


// --- PRESUPUESTOS ---

export interface PresupuestoItemOut {
  id: number;
  descripcion: string;
  procedimiento_id: string | null;
  cantidad: string;
  precio_unitario: string;
  subtotal: string;
}

export interface PresupuestoOut {
  id: string;
  turno_id: string | null;
  titulo: string;
  estado: string;
  descuento_pct: string;
  total_neto: string;
  margen_estimado: string;
  monto_reserva_cobrado: string;
  notas_internas: string;
  fecha_vencimiento: string | null;
  items: PresupuestoItemOut[];
  created_at: string;
  updated_at: string;
}

export interface PresupuestoItemIn {
  procedimiento_id?: string | null;
  descripcion_manual?: string | null;
  cantidad?: string | number;
  precio_unitario: string | number;
}

export interface PresupuestoIn {
  titulo: string;
  turno_id?: string | null;
  estado?: string;
  descuento_pct?: string | number;
  notas_internas?: string;
  fecha_vencimiento?: string | null;
  items?: PresupuestoItemIn[];
}

export interface PresupuestoGlobalOut extends PresupuestoOut {
  paciente_dni: string;
  paciente_nombre: string;
}

export interface PresupuestosGlobalResponse {
  results: PresupuestoGlobalOut[];
  count: number;
  page: number;
  page_size: number;
}

export interface PresupuestosGlobalParams {
  estado?: string;
  desde?: string;
  hasta?: string;
  paciente_dni?: string;
  q?: string;
  page?: number;
  page_size?: number;
}


export interface HistoriaClinicaEntryIn {
  tipo: string;
  titulo: string;
  contenido: string;
  metadata?: MetadataAnamnesis | Record<string, unknown>;
  turno_id?: string | null;
  firma_coords?: FirmaCoord | Record<string, unknown> | null;
}

export interface HistoriaClinicaUpdateIn {
  metadata?: MetadataAnamnesis | Record<string, unknown>;
  contenido?: string;
  completar?: boolean;
  turno_id?: string | null;
  firma_coords?: FirmaCoord | Record<string, unknown> | null;
}


// --- ADJUNTOS HISTORIA CLÍNICA ---

export interface AdjuntoHistoriaOut {
  id: string;
  entrada_id: string;
  nombre_original: string;
  tipo_mime: string;
  tamanio_bytes: number;
  url: string;
  created_at: string;
}

// --- TRANSACCIONES DE CAJA ---

export type TipoTransaccion = 'ingreso' | 'egreso'
export type OrigenTransaccion = 'manual' | 'automatico'

export interface TransaccionCajaIn {
  tipo: TipoTransaccion
  categoria: string
  monto: string | number
  metodo_pago: string
  fecha?: string
  presupuesto_id?: string | null
  proveedor_concepto?: string
  notas?: string
}

export interface TransaccionCajaOut {
  id: string
  tipo: TipoTransaccion
  categoria: string
  monto: string
  metodo_pago: string
  fecha: string
  origen: OrigenTransaccion
  proveedor_concepto: string
  notas: string
  eliminado: boolean
  presupuesto_id: string | null
  presupuesto_titulo: string | null
  paciente_dni: string | null
  paciente_nombre: string | null
  created_at: string
}

export interface TransaccionesListResponse {
  items: TransaccionCajaOut[]
  total: number
  page: number
  page_size: number
}

export interface ResumenCategoriasOut {
  categoria: string
  total: string
  count: number
}

export interface TransaccionesParams {
  tipo?: string
  desde?: string
  hasta?: string
  categoria?: string
  page?: number
  page_size?: number
}

// --- Notas Clínicas ---
export interface NotaClinicaOut {
  id: string
  contenido: string
  origen: string
  created_by: string
  created_at: string
  turno_id: string | null
}
